import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useContextMenu } from "../../../contexts/RightClickMenuContext";
import { usePopup } from "../../../contexts/PopupContext";
import ShareModal from "../ShareModal";
import LeaveGeneratingReportModal from "../../../Center/Reports/LeaveGeneratingReportModal";
import { Plus, Trash2, Pencil, Share2, FileText, Lock } from "lucide-react";

import {
  listReports,
  deleteReport,
  renameReport,
} from "../../../../api/reports";
import { apiConfig } from "../../../../api/AxiosClient";
import {
  NEW_REPORT_ID,
  DEFAULT_REPORT_TITLE,
  isUntitledReport,
} from "../../../Center/Reports/utils/reportUtils";
import { usePortalTarget } from "./sidebarUtils";
import { useLayout } from "../../../contexts/LayoutContext";
import { useSearchRegistry } from "../../Search/SearchRegistry";

const DEFAULT_TITLE = DEFAULT_REPORT_TITLE;
const isDD = process.env.REACT_APP_IS_DD === "true";

// Sidebar rule: an untitled report is never listed, with one exception —
// the report this session created on first send, which stays visible while
// it waits on its generated response. Everything else untitled is a leftover
// nobody prompted; it stays hidden until it gets a real title.
const hasContent = (r) =>
  typeof r?.report_content === "string" && r.report_content.trim().length > 0;

// Module-level cache keyed by company target. SidebarPortals remounts this
// portal (key={companyKey}) whenever the company changes — including once on
// initial load — so without this the list paints empty → filled on every
// remount. The cached list is shown immediately and refreshed in place.
const reportsCache = new Map();
const cacheKey = () => apiConfig.target ?? "__no_target__";

const sameReports = (a, b) =>
  a.length === b.length &&
  a.every(
    (r, i) =>
      r.report_id === b[i].report_id &&
      r.title === b[i].title &&
      r.report_content === b[i].report_content,
  );

const ReportsSidebarPortal = () => {
  const navigate = useNavigate();
  const portalTarget = usePortalTarget("reports-portal-slot");
  const { currentAppPage, leftOpen } = useLayout();
  const [reports, setReports] = useState(
    () => reportsCache.get(cacheKey()) || [],
  );
  const [currentReportId, setCurrentReportId] = useState(null);
  const [pendingDeleteReportId, setPendingDeleteReportId] = useState(null);
  const [renamingReportId, setRenamingReportId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const { setIsPopUpOpen, setPopUpComponent, openPopUp, closePopUp } =
    usePopup();
  // The report this session created on first send: listed as "Untitled
  // Report" while its generation is in flight, then renamed on refresh.
  const [draftReportId, setDraftReportId] = useState(null);
  // Which report MainReports is currently streaming a generation for.
  const [generatingReportId, setGeneratingReportId] = useState(null);

  useEffect(() => {
    const handler = (e) => setGeneratingReportId(e.detail?.reportId || null);
    window.addEventListener("reports:generating", handler);
    return () => window.removeEventListener("reports:generating", handler);
  }, []);

  // The user left the page mid-generation ("Stop and leave" in the route
  // guard): the draft never got its content, so its untitled row goes away.
  useEffect(() => {
    const handler = (e) => {
      const reportId = e.detail?.reportId;
      if (!reportId) return;
      setDraftReportId((d) => (d === reportId ? null : d));
      setGeneratingReportId(null);
    };
    window.addEventListener("reports:generation-abandoned", handler);
    return () =>
      window.removeEventListener("reports:generation-abandoned", handler);
  }, []);

  // ── User reports ─────────────────────────────────────────────────────────
  const loadReports = useCallback(async () => {
    // Company-scoped build with no company selected yet: the real load
    // happens on the remount that follows company selection.
    if (isDD && !apiConfig.target) return;
    try {
      const res = await listReports();
      const next = res?.data?.reports;
      if (!Array.isArray(next)) return; // keep what we have on a bad payload
      reportsCache.set(cacheKey(), next);
      // Skip the state update (and re-render) when nothing changed.
      setReports((prev) => (sameReports(prev, next) ? prev : next));
    } catch (e) {
      console.error("Error loading reports:", e); // keep current list
    }
  }, []);

  const visibleReports = useMemo(
    () =>
      reports.filter(
        (r) => r.report_id === draftReportId || !isUntitledReport(r),
      ),
    [reports, draftReportId],
  );

  // Once the draft has been titled or has content (list refreshed after
  // generation), it's a real report — stop treating it as the draft.
  useEffect(() => {
    if (!draftReportId) return;
    const draft = reports.find((r) => r.report_id === draftReportId);
    if (draft && (!isUntitledReport(draft) || hasContent(draft)))
      setDraftReportId(null);
  }, [reports, draftReportId]);

  // MainReports created the row on the user's first send: list it now.
  useEffect(() => {
    const handler = (e) => {
      const reportId = e.detail?.reportId;
      if (!reportId) return;
      setDraftReportId(reportId);
      setCurrentReportId(reportId);
      loadReports();
    };
    window.addEventListener("sidebar:report-created", handler);
    return () => window.removeEventListener("sidebar:report-created", handler);
  }, [loadReports]);

  const { register, unregister } = useSearchRegistry();
  useEffect(() => {
    register(
      "/reports",
      visibleReports.map((r) => ({
        id: r.report_id,
        label: r.title || DEFAULT_TITLE,
        keywords: [],
        icon: FileText,
        event: {
          navigateTo: "/reports",
          name: "sidebar:report-selected",
          detail: { reportId: r.report_id },
        },
      })),
    );
    return () => unregister("/reports");
  }, [visibleReports, register, unregister]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const refreshTimerRef = useRef(null);
  useEffect(() => {
    const handler = () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => loadReports(), 800);
    };
    window.addEventListener("sidebar:refresh-reports", handler);
    return () => {
      window.removeEventListener("sidebar:refresh-reports", handler);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [loadReports]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.reportId !== undefined)
        setCurrentReportId(e.detail.reportId);
    };
    window.addEventListener("sidebar:select-report", handler);
    return () => window.removeEventListener("sidebar:select-report", handler);
  }, []);

  // ── Reset selection when leaving /reports ────────────────────────────────
  // Not while a generation is streaming: the route guard (Main →
  // useGenerationLeaveGuard) may be holding the reports page under a confirm
  // dialog, and clearing the selection here would make the auto-select below
  // re-open "default" on return and abort the stream.
  useEffect(() => {
    if (currentAppPage !== "/reports" && !generatingReportId)
      setCurrentReportId(null);
  }, [currentAppPage, generatingReportId]);

  // ── Auto-select on entering /reports ─────────────────────────────────────
  // Prefer "default" as the landing state. Only auto-select a user report
  // once reports have loaded AND the user hasn't already made a selection.
  useEffect(() => {
    if (currentAppPage !== "/reports" || currentReportId) return;
    // Always land on "default" first — the user can click a report to switch.
    // If you'd prefer to land on the first user report instead, swap the lines below.
    handleSelect("default");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAppPage, currentReportId]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section && e.detail.section !== "reports")
        setCurrentReportId(null);
    };
    window.addEventListener("sidebar:active-section-changed", handler);
    return () =>
      window.removeEventListener("sidebar:active-section-changed", handler);
  }, []);

  // ── Breadcrumb ───────────────────────────────────────────────────────────
  const emitReportsBreadcrumb = useCallback(() => {
    if (currentAppPage !== "/reports") return;
    const activeReport = reports.find((r) => r.report_id === currentReportId);
    window.dispatchEvent(
      new CustomEvent("breadcrumb:update", {
        detail: {
          section: "/reports",
          subLabel: activeReport
            ? activeReport.title || DEFAULT_TITLE
            : currentReportId === NEW_REPORT_ID
              ? "New custom report"
              : null,
          subIcon: null,
          hasAdd: true,
          addKind: "reports",
          addLabel: "New custom report",
        },
      }),
    );
  }, [currentReportId, reports, currentAppPage]);

  useEffect(() => {
    emitReportsBreadcrumb();
  }, [emitReportsBreadcrumb, leftOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/reports") emitReportsBreadcrumb();
    };
    window.addEventListener("breadcrumb:request", handler);
    return () => window.removeEventListener("breadcrumb:request", handler);
  }, [emitReportsBreadcrumb]);

  // ── Leave-while-generating guard ─────────────────────────────────────────
  // MainReports aborts the stream whenever the open report changes, so any
  // click away from a generating report has to be a deliberate choice.
  const guardLeave = useCallback(
    (nextReportId, proceed) => {
      if (!generatingReportId || generatingReportId === nextReportId) {
        proceed();
        return;
      }
      openPopUp(
        <LeaveGeneratingReportModal
          onStay={closePopUp}
          onLeave={() => {
            closePopUp();
            // An abandoned draft is just another untitled row: hide it.
            if (draftReportId === generatingReportId) setDraftReportId(null);
            setGeneratingReportId(null);
            proceed();
          }}
        />,
      );
    },
    [generatingReportId, draftReportId, openPopUp, closePopUp],
  );

  // ── Actions ──────────────────────────────────────────────────────────────
  // "New custom report" opens the composer only. No row exists anywhere until
  // the user sends their first prompt (MainReports.handleSend → createReport
  // → "sidebar:report-created"), so there is nothing to add to the list here
  // and clicking New while the composer is already open is a no-op.
  const handleNew = useCallback(() => {
    guardLeave(NEW_REPORT_ID, () => {
      if (currentReportId !== NEW_REPORT_ID) {
        setCurrentReportId(NEW_REPORT_ID);
        window.dispatchEvent(
          new CustomEvent("sidebar:new-report", {
            detail: { reportId: NEW_REPORT_ID },
          }),
        );
      }
      if (currentAppPage !== "/reports") navigate("/reports");
    });
  }, [currentAppPage, currentReportId, guardLeave, navigate]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/reports") handleNew();
    };
    window.addEventListener("breadcrumb:add", handler);
    return () => window.removeEventListener("breadcrumb:add", handler);
  }, [handleNew]);

  const handleSelect = (reportId) => {
    // Re-clicking the open report would make MainReports abort any in-flight
    // generation and refetch — nothing to do but make sure we're on the page.
    if (reportId === currentReportId) {
      if (currentAppPage !== "/reports") navigate("/reports");
      return;
    }
    guardLeave(reportId, () => {
      setCurrentReportId(reportId);
      window.dispatchEvent(
        new CustomEvent("sidebar:report-selected", { detail: { reportId } }),
      );
      window.dispatchEvent(
        new CustomEvent("sidebar:active-section-changed", {
          detail: { section: "reports" },
        }),
      );
      if (currentAppPage !== "/reports") navigate("/reports");
    });
  };

  const confirmDeleteReport = async (reportId) => {
    try {
      await deleteReport(reportId);
      if (currentReportId === reportId) setCurrentReportId(null);
      if (draftReportId === reportId) setDraftReportId(null);
      loadReports();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-reports"));
    } catch (err) {
      console.error("Delete report failed:", err);
    } finally {
      setPendingDeleteReportId(null);
    }
  };

  const commitRename = async (reportId) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingReportId(null);
      return;
    }
    try {
      await renameReport(reportId, trimmed);
      loadReports();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-reports"));
    } catch (err) {
      console.error("Rename report failed:", err);
    } finally {
      setRenamingReportId(null);
    }
  };

  useContextMenu("report-item", [
    {
      label: "Share",
      icon: Share2,
      action: (payload) => {
        setPopUpComponent(
          <ShareModal
            onClose={() => setIsPopUpOpen(false)}
            contentType="report"
            documentId={payload.report_id}
            itemName={payload.title || DEFAULT_TITLE}
          />,
        );
        setIsPopUpOpen(true);
      },
    },
    {
      label: "Rename",
      icon: Pencil,
      action: (payload) => {
        setRenamingReportId(payload.report_id);
        setRenameValue(payload.title || DEFAULT_TITLE);
      },
    },
    {
      label: "Delete",
      icon: Trash2,
      variant: "danger",
      action: (payload) => {
        setPendingDeleteReportId(payload.report_id);
      },
    },
  ]);

  const isInNavbar = portalTarget?.dataset?.navbar === "true";

  const content = (
    // ── FIX: stopPropagation here prevents clicks inside the portal from
    // bubbling up to the parent sidebar nav item's onClick, which was
    // causing the "double navigation / double click" behaviour.
    <div
      className="flex flex-col gap-y-px"
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── Default Report (always present) ── */}
      {!isInNavbar && (
        <button
          onClick={() => handleSelect("default")}
          className={[
            "w-full text-left rounded-md px-2 py-1.5 text-[12px] truncate transition-colors flex items-center gap-x-1.5",
            currentReportId === "default"
              ? "text-[#0EA5E9] bg-[#0EA5E9]/[0.08] font-medium"
              : "text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/40",
          ].join(" ")}
        >
          <Lock className="h-3 w-3 shrink-0 opacity-60" strokeWidth={2} />
          <span className="truncate">Default Report</span>
        </button>
      )}

      {/* ── New report button ── */}
      {!isInNavbar && (
        <button
          onClick={handleNew}
          className="group w-full flex items-center gap-x-2 rounded-md px-2 py-1.5 text-[12px] text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800/60 transition-colors"
          title="New custom report"
        >
          <Plus className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          <span>New custom report</span>
        </button>
      )}

      {/* ── User reports ── */}
      {visibleReports.map((r) => {
        const isActive = r.report_id === currentReportId;
        const isRenaming = renamingReportId === r.report_id;
        const isPendingDelete = pendingDeleteReportId === r.report_id;

        return (
          <div
            key={r.report_id}
            data-context-menu="report-item"
            data-context-payload={JSON.stringify(r)}
          >
            {isRenaming ? (
              <div className="flex items-center gap-x-1 px-2 py-1">
                <input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRename(r.report_id);
                    if (e.key === "Escape") setRenamingReportId(null);
                  }}
                  onBlur={() => commitRename(r.report_id)}
                  className="flex-1 min-w-0 bg-transparent text-[12px] text-slate-700 dark:text-gray-200 border border-[#0EA5E9]/30 rounded px-1.5 py-1 outline-none focus:border-[#0EA5E9]/60"
                />
              </div>
            ) : (
              <button
                onClick={() => handleSelect(r.report_id)}
                title={r.title}
                className={[
                  "w-full text-left rounded-md px-2 py-1.5 text-[12px] truncate transition-colors flex items-center gap-x-1.5",
                  isActive
                    ? "text-[#0EA5E9] bg-[#0EA5E9]/[0.08] font-medium"
                    : "text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/40",
                ].join(" ")}
              >
                <span className="truncate">{r.title || DEFAULT_TITLE}</span>
              </button>
            )}

            <div
              className={[
                "overflow-hidden transition-all duration-200 ease-in-out",
                isPendingDelete
                  ? "max-h-[80px] opacity-100 mt-0.5 mb-0.5"
                  : "max-h-0 opacity-0",
              ].join(" ")}
            >
              {isPendingDelete && (
                <div className="flex items-center gap-x-2 py-1 px-1">
                  <span className="text-[11px] text-slate-500 dark:text-gray-400">
                    Delete?
                  </span>
                  <button
                    onClick={() => setPendingDeleteReportId(null)}
                    className="text-[11px] px-2 py-0.5 rounded border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => confirmDeleteReport(r.report_id)}
                    className="text-[11px] px-2 py-0.5 rounded border border-red-300 dark:border-red-500/30 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                  >
                    Confirm
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

    </div>
  );

  if (!portalTarget) return null;
  return createPortal(content, portalTarget);
};

export default ReportsSidebarPortal;
